import axios from "axios";
import { API_URL, COMPANY_ID } from "../config";
import { flatDeep } from "../utils";
import { user } from "./actions_helper";
import {
    CREATE_INVENTORY_ITEM,
    DELETE_INVENTORY_ITEM,
    GET_INVENTORY_ITEMS,
    INVENTORY_ITEM_LOADING_START,
    INVENTORY_ITEM_LOADING_STOP,
    UPDATE_INVENTORY_ITEM,
} from "./types";

interface IFetchType {
    page?: number;
    page_size?: number;
    order_by?: string;
    order_dir?: string;
    hidden_fields?: any;
    ids?: Array<number>;
    approved?: boolean | string;
    ai_run_id?: number | string;
}

export function getInventoryItems(opts: IFetchType = {}, type = "repair") {
    const qsOpts = {
        page: opts.page || 1,
        page_size: opts.page_size || null,
        order_by: opts.order_by || "name",
        order_dir: opts.order_dir || "asc",
        approved: opts.approved != null ? String(opts.approved) : null,
        ai_run_id: opts.ai_run_id ? String(opts.ai_run_id) : null,
    };
    const qs = Object.keys(qsOpts)
        .map((k) => {
            return qsOpts[k] != null ? `${k}=${qsOpts[k]}` : null;
        })
        .filter(String)
        .join("&");

    // const types = [type].flatMap((t) => `type[]=${t}`).join("&"); # "target": "es6", "lib": [ "es2019"
    const types = flatDeep([type])
        .map((t) => `type[]=${t}`)
        .join("&");
    return async (dispatch: IDispatch<IReduxAction<JSON>>) => {
        const response = await axios.get(
            `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?${types}&${qs}`,
            {
                headers: { authorization: user().token },
            }
        );
        dispatch({
            type: GET_INVENTORY_ITEMS,
            payload: response.data,
        });
        return response.data;
    };
}

export const getInventoryItemsAJAX = (opts: IFetchType = {}, type: any = "repair") => {
    const page = opts.page || 1;
    const pageSize = opts.page_size || -1;
    const orderBy = opts.order_by || "name";
    const orderDir = opts.order_dir || "asc";
    const hiddenFields =
        opts.hidden_fields ||
        "total_buying_price,total_sales_price,limit_value,total_pages,current_page,next_page,prev_page,first_page?,last_page?,out_of_range?,total_quantity,count";
    const idsQuery = Array.isArray(opts.ids)
        ? opts.ids.map((id) => `ids[]=${encodeURIComponent(String(id))}`).join("&")
        : "";

    const deviceToken = localStorage.getItem("deviceToken");
    // const types = [type].flatMap((t) => `type[]=${t}`).join("&"); # "target": "es6", "lib": [ "es2019"
    const types = flatDeep([type])
        .map((t) => `type[]=${t}`)
        .join("&");
    return axios.get(
        `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?${types}&${idsQuery}&page=${page}&page_size=${pageSize}&order_by=${orderBy}&order_dir=${orderDir}&hidden_fields=${hiddenFields}`,
        {
            headers: { authorization: deviceToken ?? user().token },
        }
    );
};

export const getUnapprovedInventoryItemsCountAJAX = async () => {
    const response = await axios.get(
        `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?approved=false&page_size=1&hidden_fields=total,total_quantity,total_buying_price,total_sales_price`,
        {
            headers: { authorization: user().token },
        }
    );
    return Number(response?.data?.count) || 0;
};

export const getInventoryItemsForFormAJAX = (opts: IFetchType = {}, id: any) => {
    const orderBy = opts.order_by || "name";
    const orderDir = opts.order_dir || "asc";
    // The server's `page_size=-1` special-case calls `max_page_count`, which counts items
    // by `inventory_type` — but the form-scoped path doesn't send `type`, so it ends up
    // counting `where(inventory_type: nil) = 0` and clamps to 1. Use a literal large value
    // to bypass the special-case entirely and let Kaminari's `.per(N)` return all matches.
    const pageSize = opts.page_size && opts.page_size > 0 ? opts.page_size : 99999;
    const deviceToken = localStorage.getItem("deviceToken");

    return axios.get(
        `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?form_id=${id}&order_by=${orderBy}&order_dir=${orderDir}&page_size=${pageSize}`,
        {
            headers: { authorization: deviceToken ?? user().token },
        }
    );
};

export function createInventoryItemAJAX(inventoryItem: any, type = "repair") {
    return async (dispatch: IDispatch<IReduxAction<JSON>>) => {
        dispatch({ type: INVENTORY_ITEM_LOADING_START });
        axios
            .post(`${API_URL}/inventory_items.json`, {
                inventory_item: {
                    ...inventoryItem,
                    company_id: COMPANY_ID,
                    type,
                },
            })
            .then((res) => {
                dispatch({
                    type: CREATE_INVENTORY_ITEM,
                    payload: res.data,
                });
                getInventoryItems()(dispatch);
            })
            .catch((err) => {
                throw err;
            })
            .finally(() => {
                dispatch({ type: INVENTORY_ITEM_LOADING_STOP });
            });
    };
}

export function approveInventoryItem(inventoryItem: any) {
    return async (dispatch: IDispatch<IReduxAction<any>>) => {
        dispatch({ type: INVENTORY_ITEM_LOADING_START });
        try {
            const res = await axios.patch(
                `${API_URL}/inventory_items/${inventoryItem.id}.json`,
                { inventory_item: { approved: true } },
                { headers: { authorization: user().token } }
            );
            dispatch({ type: UPDATE_INVENTORY_ITEM, payload: res.data });
            return res.data;
        } finally {
            dispatch({ type: INVENTORY_ITEM_LOADING_STOP });
        }
    };
}

export function deleteInventoryItem(inventoryItem: any) {
    return async (dispatch: IDispatch<IReduxAction<any>>) => {
        dispatch({ type: INVENTORY_ITEM_LOADING_START });
        axios
            .delete(`${API_URL}/inventory_items/${inventoryItem.id}.json`, {
                headers: { authorization: user().token },
            })
            .then((response) => {
                dispatch({
                    type: DELETE_INVENTORY_ITEM,
                    payload: {},
                });
                getInventoryItems({}, inventoryItem.inventory_type)(dispatch);
            })
            .catch((err) => {
                throw err;
            })
            .finally(() => {
                dispatch({ type: INVENTORY_ITEM_LOADING_STOP });
            });
    };
}
